/**
 * ActionSimulator - アクションシミュレータ
 *
 * ゲームアクション（魔法使用、召喚、スキル使用）をシミュレートし、
 * 結果の盤面状態を返す。純粋関数として実装。
 */

import type { GameState } from '../../gameEngine/GameState';
import { getPlayer, updatePlayer } from '../../gameEngine/GameState';
import type { CardWithId } from '../../../types/card';
import type { PlayerNumber } from '../../../types/player';
import { createPureContext } from '../../gameEngine/contextAdapter';
import { executeSkillEffects } from '../../effectEngine';
import { createMonsterInstance } from '../../../utils/helpers';
import { registerCardTriggersPure, fireTriggerPure } from '../../gameEngine/triggerEnginePure';
import { TRIGGER_TYPES } from '../../triggerTypes';

// =============================================================================
// 型定義
// =============================================================================

/** シミュレーション結果 */
export interface SimulationResult {
  /** シミュレーション成功 */
  success: boolean;
  /** 結果の状態（成功時のみ） */
  state: GameState | null;
  /** エラーメッセージ（失敗時） */
  error?: string;
}

// =============================================================================
// 魔法カードシミュレーション
// =============================================================================

/**
 * 魔法カード使用をシミュレート
 *
 * 純粋関数：状態を変更せず、新しい状態を返す
 *
 * @param state - 現在のゲーム状態
 * @param card - 使用する魔法カード
 * @param player - 使用プレイヤー
 * @returns シミュレーション結果
 */
export function simulateMagicUse(
  state: GameState,
  card: CardWithId,
  player: PlayerNumber
): SimulationResult {
  try {
    const playerData = getPlayer(state, player);

    // バリデーション
    if (!card || card.type !== 'magic') {
      return {
        success: false,
        state: null,
        error: '魔法カードではありません',
      };
    }

    // SP確認
    const cost = card.cost || 0;
    const totalAvailableSP = playerData.activeSP + (playerData.bonusSP || 0);
    if (cost > totalAvailableSP) {
      return {
        success: false,
        state: null,
        error: 'SPが足りません',
      };
    }

    // 魔法ブロック確認
    if (playerData.magicBlocked) {
      return {
        success: false,
        state: null,
        error: '魔法がブロックされています',
      };
    }

    // 手札に対象カードがあるか確認
    const cardInHand = playerData.hand.find(c => c.uniqueId === card.uniqueId);
    if (!cardInHand) {
      return {
        success: false,
        state: null,
        error: '手札にカードがありません',
      };
    }

    // --- シミュレーション開始 ---

    // 1. 手札からカードを削除
    const newHand = playerData.hand.filter(c => c.uniqueId !== card.uniqueId);

    // 2. SPを消費（activeSP優先、足りなければbonusSP使用）
    const fromActive = Math.min(playerData.activeSP, cost);
    const fromBonus = cost - fromActive;
    const newActiveSP = playerData.activeSP - fromActive;
    const newRestedSP = playerData.restedSP + fromActive;
    const newBonusSP = (playerData.bonusSP || 0) - fromBonus;

    // 3. 墓地に送る
    const newGraveyard = [...playerData.graveyard, card];

    // 4. 状態を更新
    let newState = updatePlayer(state, player, {
      hand: newHand,
      activeSP: newActiveSP,
      restedSP: newRestedSP,
      bonusSP: newBonusSP,
      graveyard: newGraveyard,
    });

    // 5. カード効果を実行（contextAdapterを使用）
    const effectText = card.effect || '';
    if (effectText) {
      const context = createPureContext(newState, {
        card,
      });

      // 効果を実行（カード固有効果 or 汎用パーサー）
      // PureContextはExecuteEffectContextと互換性があるが型定義が異なるためキャスト
      executeSkillEffects(effectText, context as unknown as Parameters<typeof executeSkillEffects>[1], card.id);

      // 更新されたstateを取得
      newState = context.getState();
    }

    // 6. 魔法発動トリガーを発火
    newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_SELF_MAGIC_ACTIVATED, { card });

    return {
      success: true,
      state: newState,
    };
  } catch (error) {
    return {
      success: false,
      state: null,
      error: `シミュレーションエラー: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

// =============================================================================
// 召喚シミュレーション
// =============================================================================

/**
 * モンスター召喚をシミュレート
 *
 * @param state - 現在のゲーム状態
 * @param card - 召喚するカード
 * @param slotIndex - 召喚先スロット（0-4）
 * @param player - 召喚プレイヤー
 * @returns シミュレーション結果
 */
export function simulateSummon(
  state: GameState,
  card: CardWithId,
  slotIndex: number,
  player: PlayerNumber
): SimulationResult {
  try {
    const playerData = getPlayer(state, player);

    // バリデーション
    if (!card || card.type !== 'monster') {
      return {
        success: false,
        state: null,
        error: 'モンスターカードではありません',
      };
    }

    // スロット確認
    if (slotIndex < 0 || slotIndex > 4) {
      return {
        success: false,
        state: null,
        error: '無効なスロットです',
      };
    }

    if (playerData.field[slotIndex] !== null) {
      return {
        success: false,
        state: null,
        error: 'スロットが空いていません',
      };
    }

    // SP確認
    const cost = card.cost || 0;
    const totalAvailableSP = playerData.activeSP + (playerData.bonusSP || 0);
    if (cost > totalAvailableSP) {
      return {
        success: false,
        state: null,
        error: 'SPが足りません',
      };
    }

    // 手札に対象カードがあるか確認
    const cardInHand = playerData.hand.find(c => c.uniqueId === card.uniqueId);
    if (!cardInHand) {
      return {
        success: false,
        state: null,
        error: '手札にカードがありません',
      };
    }

    // --- シミュレーション開始 ---

    // 1. モンスターインスタンス生成
    const monster = createMonsterInstance(card);
    monster.owner = player;
    monster.canAttack = false; // 召喚酔い

    // 2. 手札からカードを削除
    const newHand = playerData.hand.filter(c => c.uniqueId !== card.uniqueId);

    // 3. フィールドにモンスターを配置
    const newField = [...playerData.field];
    newField[slotIndex] = monster;

    // 4. SPを消費
    const fromActive = Math.min(playerData.activeSP, cost);
    const fromBonus = cost - fromActive;
    const newActiveSP = playerData.activeSP - fromActive;
    const newRestedSP = playerData.restedSP + fromActive;
    const newBonusSP = (playerData.bonusSP || 0) - fromBonus;

    // 5. 状態を更新
    let newState = updatePlayer(state, player, {
      hand: newHand,
      field: newField,
      activeSP: newActiveSP,
      restedSP: newRestedSP,
      bonusSP: newBonusSP,
    });

    // 6. トリガー登録
    newState = registerCardTriggersPure(newState, monster, player, slotIndex);

    // 7. ON_SUMMONトリガー発火
    newState = fireTriggerPure(newState, TRIGGER_TYPES.ON_SUMMON, {
      card: monster,
      slotIndex,
    });

    return {
      success: true,
      state: newState,
    };
  } catch (error) {
    return {
      success: false,
      state: null,
      error: `シミュレーションエラー: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

// =============================================================================
// スキル使用シミュレーション
// =============================================================================

/**
 * スキル使用をシミュレート
 *
 * @param state - 現在のゲーム状態
 * @param monsterIndex - スキルを使用するモンスターのスロット
 * @param skillType - 基本技 or 上級技
 * @param player - プレイヤー
 * @returns シミュレーション結果
 */
export function simulateSkillUse(
  state: GameState,
  monsterIndex: number,
  skillType: 'basic' | 'advanced',
  player: PlayerNumber
): SimulationResult {
  try {
    const playerData = getPlayer(state, player);
    const monster = playerData.field[monsterIndex];

    // バリデーション
    if (!monster) {
      return {
        success: false,
        state: null,
        error: 'モンスターが見つかりません',
      };
    }

    // 技使用済み確認
    if (monster.usedSkillThisTurn) {
      return {
        success: false,
        state: null,
        error: 'このターン既に技を使用しています',
      };
    }

    // チャージ確認
    const requiredCharges = skillType === 'basic' ? 1 : 2;
    const chargesCount = monster.charges?.length || 0;
    if (chargesCount < requiredCharges) {
      return {
        success: false,
        state: null,
        error: `チャージが足りません（必要: ${requiredCharges}, 現在: ${chargesCount}）`,
      };
    }

    // スキルテキストを取得
    const skillData = skillType === 'basic' ? monster.basicSkill : monster.advancedSkill;
    const skillText = typeof skillData === 'object' ? skillData?.text : skillData;

    if (!skillText) {
      return {
        success: false,
        state: null,
        error: '技が見つかりません',
      };
    }

    // --- シミュレーション開始 ---

    // 1. チャージを消費
    const newCharges = [...(Array.isArray(monster.charges) ? monster.charges : [])];
    for (let i = 0; i < requiredCharges; i++) {
      newCharges.shift();
    }

    // 2. 技使用済みフラグを設定
    const newField = [...playerData.field];
    newField[monsterIndex] = {
      ...monster,
      usedSkillThisTurn: true,
      charges: newCharges,
    };

    // 3. 状態を更新
    let newState = updatePlayer(state, player, { field: newField });

    // 4. contextアダプターを使用して技効果を実行
    const context = createPureContext(newState, {
      monsterIndex,
      skillType,
      card: monster,
    });

    // 技効果を実行
    // PureContextはExecuteEffectContextと互換性があるが型定義が異なるためキャスト
    executeSkillEffects(skillText, context as unknown as Parameters<typeof executeSkillEffects>[1], monster.id);

    // 更新されたstateを取得
    newState = context.getState();

    return {
      success: true,
      state: newState,
    };
  } catch (error) {
    return {
      success: false,
      state: null,
      error: `シミュレーションエラー: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}
